// ════════════════════════════════════════════════════════════════════════════
//  TeacherAgendaTab.jsx  ·  Hub Académico del Alumno — REDISEÑO v4
//  Carpeta: src/components/dashboard/teacher_agenda/
//
//  CAMBIOS v4 (Day-First UX):
//    ① Carga siempre en HOY — nunca dump de 116 clases
//    ② Navegación por semanas con ← → (no solo la semana actual)
//    ③ Siglas de 3 letras: Lun Mar Mié Jue Vie Sáb Dom
//    ④ Días pasados desbloqueados — muestran historial al tocar
//    ⑤ Botón "Ver todas futuras" colapsable en la sección
//    ⑥ Layout desktop 2 columnas (hero izquierda, calendario derecha)
//    ⑦ Política 24h preservada — LockedModal intacto
//
//  Backend compatible: mismos endpoints que v3 (sin cambios en API)
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './TeacherAgendaTab.css';
import { enlaceWhatsApp } from '../../../contacto.js';

// ─────────────────────────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTHS_LONG = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Unicode escape sequences — no dependen del encoding del archivo fuente
const E = {
    calendar:  '\u{1F4C5}',                          // 📅
    teacher:   '\u{1F469}\u200D\u{1F3EB}',           // 👩🏫
    clock:     '\u{1F550}',                          // 🕐
    cross:     '\u{274C}',                           // ❌
    pause:     '\u{23F8}\uFE0F',                     // ⏸️
    phone:     '\u{1F4F1}',                          // 📱
    check:     '\u{2705}',                           // ✅
    warn:      '\u26A0\uFE0F',                       // ⚠️
    info:      '\u{2139}\uFE0F',                     // ℹ️
    pin:       '\u{1F4CD}',                          // 📍
    pencil:    '\u{270F}\uFE0F',                     // ✏️
    star:      '\u2B50',                             // ⭐
    fire:      '\u{1F525}',                          // 🔥
};

// v4: siglas de 3 letras para eliminar ambigüedad M/M
const DAYS_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAYS_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const STATUS_META = {
    assigned: { label: 'Próxima', color: '#f5a623', icon: '📅', pill: 'rgba(245,166,35,.15)' },
    completed: { label: 'Completada', color: '#00e5ff', icon: '✅', pill: 'rgba(0,229,255,.15)' },
    cancelled: { label: 'Cancelada', color: '#ff5e7e', icon: '✕', pill: 'rgba(255,94,126,.15)' },
    postponed: { label: 'Pospuesta', color: '#c084fc', icon: '⏸', pill: 'rgba(192,132,252,.15)' },
};

// ─────────────────────────────────────────────────────────────────────────────
//  DATE HELPERS — CST México UTC-6 permanente
// ─────────────────────────────────────────────────────────────────────────────
function getCSTNow() {
    const now = new Date();
    return new Date(now.getTime() + now.getTimezoneOffset() * 60000 - 6 * 3600000);
}

/** Lunes de la semana actual + offset de semanas */
// Cuántas semanas hay entre esta y la de `iso`: para saltar a su día desde un
// aviso de cambio, que puede ser de la semana que viene o de dentro de un mes.
function offsetDeSemana(iso) {
    if (!iso) return 0;
    const lunesHoy = getMondayForOffset(0);
    const [y, m, d] = iso.split('-').map(Number);
    const f = new Date(y, m - 1, d);
    const dia = f.getDay();
    f.setDate(f.getDate() - dia + (dia === 0 ? -6 : 1));
    f.setHours(0, 0, 0, 0);
    return Math.round((f - lunesHoy) / (7 * 24 * 3600 * 1000));
}

function getMondayForOffset(offset = 0) {
    const d = getCSTNow();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d);
    monday.setDate(diff + offset * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
}

function formatDateISO(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── LA HORA, CON SUS MINUTOS ────────────────────────────────────────────────
// Esto solo sabía de horas en punto: las clases particulares se agendan así.
// Las de empresa no —gerencia pone «18:30»— y una clase de las 6:30 se pintaba
// como «6:00 pm». Media hora de diferencia es llegar tarde o llegar para nada.
//
// Acepta el `time` («HH:MM») cuando viene, y sigue aceptando el número de
// siempre para lo demás.
function formatHour(h, time) {
    let n = Number(h);
    let min = 0;
    if (typeof time === 'string' && /^\d{1,2}:\d{2}$/.test(time)) {
        const [hh, mm] = time.split(':').map(Number);
        n = hh;
        min = mm;
    }
    if (!Number.isFinite(n)) return '—';
    const mm = String(min).padStart(2, '0');
    if (n === 0) return `12:${mm} am`;
    if (n < 12) return `${n}:${mm} am`;
    if (n === 12) return `12:${mm} pm`;
    return `${n - 12}:${mm} pm`;
}

function formatDateLong(iso) {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const dow = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][date.getDay()];
    return `${dow.charAt(0).toUpperCase() + dow.slice(1)}, ${d} de ${MONTHS_LONG[m - 1]}`;
}

function formatDateShort(iso) {
    if (!iso) return '—';
    const [, m, d] = iso.split('-').map(Number);
    return `${d} ${MONTHS_ES[m - 1]}`;
}

function diffDays(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    const today = getCSTNow();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / 86_400_000);
}

function diffHours(cls) {
    if (!cls.date || cls.hour == null) return null;
    // Con los minutos de verdad: si no, una clase de las 18:30 se contaba
    // como de las 18:00 al medir las 24 horas de preaviso.
    const hhmm = /^\d{1,2}:\d{2}$/.test(String(cls.time || ''))
        ? String(cls.time).padStart(5, '0')
        : `${String(cls.hour).padStart(2, '0')}:00`;
    const dt = new Date(`${cls.date}T${hhmm}:00-06:00`);
    return (dt - Date.now()) / 3_600_000;
}

function isWindowClosed(cls) {
    const h = diffHours(cls);
    return h !== null && h < 24;
}

function urgencyLevel(cls) {
    const h = diffHours(cls);
    if (h === null) return 'future';
    if (h < -1) return 'past';
    if (h <= 0) return 'now';
    if (h <= 2) return 'imminent';

    // Comparar fecha calendario en CST, no horas brutas
    const todayISO = formatDateISO(getCSTNow());
    const tmrw = getCSTNow();
    tmrw.setDate(tmrw.getDate() + 1);
    const tomorrowISO = formatDateISO(tmrw);

    if (cls.date === todayISO) return 'today';
    if (cls.date === tomorrowISO) return 'tomorrow';
    return 'future';
}

// 🔒 Capa 1: Fuente única de verdad para el estado efectivo de una clase.
// La DB puede decir 'assigned' pero si ya pasó (>1h) → es 'completed'.
function resolveStatus(cls) {
    if (cls.status !== 'assigned') return cls.status; // cancelled/postponed → respetar siempre
    const h = diffHours(cls);
    if (h !== null && h < -1) return 'completed';     // pasó hace >1h → auto-completada
    return 'assigned';                                 // aún vigente
}

/** Etiqueta de semana amigable */
function getWeekLabel(offset, weekDates) {
    if (offset === 0) return 'Esta semana';
    if (offset === 1) return 'Próxima semana';
    if (offset === -1) return 'Semana pasada';
    const [, fm, fd] = weekDates[0].split('-').map(Number);
    const [, lm, ld] = weekDates[6].split('-').map(Number);
    if (fm === lm) return `${fd}–${ld} ${MONTHS_ES[fm - 1]}`;
    return `${fd} ${MONTHS_ES[fm - 1]} – ${ld} ${MONTHS_ES[lm - 1]}`;
}

const _PALETTE = ['#6366f1', '#0891b2', '#059669', '#f59e0b', '#ec4899', '#ef4444', '#c084fc', '#f472b6'];
const avatarColor = (id) => _PALETTE[(id || 0) % _PALETTE.length];

function initials(name) {
    return (name || '?').trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
}

// ─────────────────────────────────────────────────────────────────────────────
//  DATA HOOK (sin cambios — compatible con API existente)
// ─────────────────────────────────────────────────────────────────────────────
function useTeacherAgenda() {
    const [teacher, setTeacher] = useState(null);
    const [classes, setClasses] = useState([]);
    const [stats, setStats] = useState({ upcoming: 0, completed: 0, cancelled: 0, streak: 0 });
    // Lo que gerencia cambió de sus clases y aún no ha visto (19-09-2026).
    const [cambios, setCambios] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const headers = useCallback(() => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
    }), []);

    const autoCompletePastClasses = useCallback(async (classList) => {
        const pastAssigned = classList.filter(c => {
            if (c.status !== 'assigned' || !c.id) return false;
            const h = diffHours(c);
            return h !== null && h < -1;
        });
        if (pastAssigned.length === 0) return;

        // Marcar cada una en background — sin bloquear UI
        await Promise.allSettled(
            pastAssigned.map(c =>
                // El prefijo correcto es `/api/admin/student/…`: el router
                // vive bajo `/api/admin` aunque la ruta la use el alumno (su
                // guardia es `get_current_user`, no admin). Con el path viejo
                // esto era un 404 que `Promise.allSettled` se tragaba, y
                // NINGUNA clase llegaba a marcarse.
                //
                // Desde ahora el servidor las marca solo al leer la agenda
                // (`services/clases_dadas.py`), asi que esta llamada es un
                // refuerzo, no la unica via.
                fetch(`/api/admin/student/classes/${c.id}/complete`, {
                    method: 'POST',
                    headers: headers(),
                })
            )
        );
    }, [headers]);

    const fetchAgenda = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/api/student/agenda', { headers: headers() });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const classList = Array.isArray(data.classes) ? data.classes : [];
            setTeacher(data.teacher ?? null);
            setClasses(classList);
            setStats(data.stats ?? { upcoming: 0, completed: 0, cancelled: 0, streak: 0 });
            setCambios(Array.isArray(data.cambios) ? data.cambios : []);

            // 🔒 Auto-completar en background — no bloquea, no lanza error al usuario
            autoCompletePastClasses(classList).catch(() => { });

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [headers, autoCompletePastClasses]);

    const postponeClass = useCallback(async (classId, newDate, reason) => {
        const res = await fetch(`/api/student/classes/${classId}/postpone`, {
            method: 'POST', headers: headers(),
            body: JSON.stringify({ new_date: newDate, reason }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'No se pudo posponer la clase');
        }
        await fetchAgenda();
    }, [fetchAgenda, headers]);

    const cancelClass = useCallback(async (classId, reason) => {
        const res = await fetch(`/api/student/classes/${classId}/cancel`, {
            method: 'POST', headers: headers(),
            body: JSON.stringify({ reason }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'No se pudo cancelar');
        }
        await fetchAgenda();
    }, [fetchAgenda, headers]);

    // «Entendido»: gerencia lo ve como «visto» en el CRM y deja de tener que
    // escribirle por WhatsApp. Se quita de la pantalla al instante y se pide
    // la agenda otra vez para que la tarjeta de la clase también lo sepa.
    const marcaVisto = useCallback(async (cambioId) => {
        setCambios((prev) => prev.filter((c) => c.id !== cambioId));
        try {
            await fetch(`/api/student/cambios-clase/${cambioId}/visto`, {
                method: 'POST', headers: headers(),
            });
        } finally {
            fetchAgenda();
        }
    }, [fetchAgenda, headers]);

    useEffect(() => { fetchAgenda(); }, [fetchAgenda]);

    return { teacher, classes, stats, cambios, marcaVisto, isLoading, error, refetch: fetchAgenda, postponeClass, cancelClass };
}

// ─────────────────────────────────────────────────────────────────────────────
//  SKELETON
// ─────────────────────────────────────────────────────────────────────────────
function Skel({ w = '100%', h = 14, br = 6, style = {} }) {
    return (
        <span
            className="ta-skel"
            style={{ width: w, height: h, borderRadius: br, display: 'inline-block', ...style }}
        />
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TOAST
// ─────────────────────────────────────────────────────────────────────────────
function Toast({ msg, type = 'ok', onDone }) {
    useEffect(() => { const t = setTimeout(onDone, 3200); return () => clearTimeout(t); }, [onDone]);
    const C = {
        ok: { bg: 'rgba(0,204,102,.1)', border: 'rgba(0,204,102,.35)', text: '#00cc66' },
        err: { bg: 'rgba(255,94,126,.1)', border: 'rgba(255,94,126,.35)', text: '#ff5e7e' },
        wrn: { bg: 'rgba(245,166,35,.1)', border: 'rgba(245,166,35,.35)', text: '#f5a623' },
    }[type] || { bg: 'rgba(0,204,102,.1)', border: 'rgba(0,204,102,.35)', text: '#00cc66' };

    return (
        <div className="ta-toast" style={{ background: C.bg, borderColor: C.border, color: C.text }}>
            <span>{type === 'ok' ? '✅' : type === 'err' ? '❌' : '⚠️'}</span>
            <span>{msg}</span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TEACHER CHIP
// ─────────────────────────────────────────────────────────────────────────────
function TeacherChip({ teacher, isLoading }) {
    if (isLoading) {
        return (
            <div className="ta-teacher-chip">
                <Skel w={38} h={38} br={10} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                    <Skel w="35%" h={13} />
                    <Skel w="25%" h={10} />
                </div>
            </div>
        );
    }
    if (!teacher) return null;

    const color = teacher.color || avatarColor(teacher.id);
    return (
        <div className="ta-teacher-chip">
            <div className="ta-chip-avatar" style={{ background: `${color}18`, color }}>
                {teacher.ini || initials(teacher.name)}
                {teacher.is_online && <span className="ta-chip-online-dot" />}
            </div>
            <div className="ta-chip-info">
                <span className="ta-chip-name">{teacher.name}</span>
                <span className="ta-chip-meta">
                    {teacher.lang}
                    {teacher.is_online && (
                        <span className="ta-chip-online-badge">● EN LÍNEA</span>
                    )}
                </span>
            </div>
            <div className="ta-chip-rating">
                <span className="ta-chip-star">★</span>
                <span>{(teacher.rating || 5).toFixed(1)}</span>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  STATS ROW
// ─────────────────────────────────────────────────────────────────────────────
function StatsRow({ stats, isLoading, weekCount }) {
    const tiles = [
        { icon: '🔥', val: stats?.streak ?? 0, label: 'Racha', color: '#f5a623', glow: 'rgba(245,166,35,.3)' },
        { icon: '✅', val: stats?.completed ?? 0, label: 'Completadas', color: '#00e5ff', glow: 'rgba(0,229,255,.25)' },
        { icon: '📅', val: weekCount ?? 0, label: 'Esta semana', color: '#00cc66', glow: 'rgba(0,204,102,.25)' },
        { icon: '📚', val: stats?.upcoming ?? 0, label: 'Pendientes', color: '#c084fc', glow: 'rgba(192,132,252,.25)' },
    ];

    return (
        <div className="ta-stats-row">
            {tiles.map((t) => (
                <div key={t.label} className="ta-stat-tile">
                    <div className="ta-stat-icon">{t.icon}</div>
                    <div className="ta-stat-val" style={{ '--stat-color': t.color }}>
                        {isLoading ? <Skel w={28} h={22} br={4} /> : t.val}
                    </div>
                    <div className="ta-stat-lbl">{t.label}</div>
                    <div className="ta-stat-glow" style={{ background: t.glow }} />
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  URGENCY BADGE
// ─────────────────────────────────────────────────────────────────────────────
function UrgencyBadge({ cls }) {
    const level = urgencyLevel(cls);
    const d = diffDays(cls.date);
    const dh = diffHours(cls);

    const configs = {
        past: {
            label: 'Clase dada',
            bg: 'rgba(128,128,128,.1)',
            border: 'rgba(128,128,128,.25)',
            color: 'var(--text-secondary)',
            pulse: false
        },
        now: { label: '¡Ahora mismo!', bg: 'rgba(0,204,102,.18)', border: 'rgba(0,204,102,.5)', color: '#00cc66', pulse: true },
        imminent: { label: `En ${Math.ceil(dh)}h`, bg: 'rgba(255,94,126,.15)', border: 'rgba(255,94,126,.45)', color: '#ff5e7e', pulse: true },
        today: { label: 'Hoy', bg: 'rgba(245,166,35,.15)', border: 'rgba(245,166,35,.4)', color: '#f5a623', pulse: false },
        tomorrow: { label: 'Mañana', bg: 'rgba(192,132,252,.12)', border: 'rgba(192,132,252,.35)', color: '#c084fc', pulse: false },
        future: { label: d !== null ? `En ${d}d` : 'Próxima', bg: 'rgba(128, 128, 128, 0.08)', border: 'rgba(128, 128, 128, 0.2)', color: 'var(--text-secondary)', pulse: false },
    };

    const c = configs[level] || configs.future;
    return (
        <span
            className={`ta-urgency-badge${c.pulse ? ' ta-urgency-badge--pulse' : ''}`}
            style={{ background: c.bg, borderColor: c.border, color: 'var(--text-primary)' }}
        >
            {c.label}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  NEXT CLASS HERO
// ─────────────────────────────────────────────────────────────────────────────
function NextClassHero({ cls, onEnter, onPostpone, onCancel, onLocked, onAviso }) {
    // La tarjeta grande es la que ve primero el alumno, y era la que Rob
    // tenía delante: ofrecía «Posponer» y «Cancelar Clase» a un empleado de
    // empresa, que no puede hacer ni lo uno ni lo otro. Aquí se decide con
    // el mismo dato que la tarjeta pequeña, para que las dos digan lo mismo.
    const deEmpresa = cls?.origen === 'empresa';
    if (!cls) {
        return (
            <div className="ta-next-hero ta-next-hero--empty">
                <div className="ta-next-hero-empty-icon">🎯</div>
                <div className="ta-next-hero-empty-title">Sin clases programadas</div>
                <div className="ta-next-hero-empty-sub">
                    Tu coordinador agendará tus próximas clases pronto.
                    ¡Mientras tanto, sigue practicando!
                </div>
            </div>
        );
    }

    const level = urgencyLevel(cls);
    const locked = isWindowClosed(cls);
    const dh = diffHours(cls);
    const isNow = level === 'now' || level === 'imminent';
    const isToday = level === 'today' || isNow;

    const glowMap = {
        now: 'rgba(0,204,102,.35)',
        imminent: 'rgba(255,94,126,.35)',
        today: 'rgba(245,166,35,.28)',
        tomorrow: 'rgba(192,132,252,.22)',
        future: 'rgba(0,255,255,.12)',
    };

    const handleModify = (fn) => () => {
        if (locked) { onLocked(cls); return; }
        fn(cls);
    };

    return (
        <div
            className={`ta-next-hero${isNow ? ' ta-next-hero--now' : ''}`}
            style={{ '--hero-glow': glowMap[level] || glowMap.future }}
        >
            <div className="ta-next-hero-eyebrow">
                {isNow ? '🟢 TU CLASE COMIENZA AHORA' : isToday ? '⏰ PRÓXIMA CLASE · HOY' : '🗓️ PRÓXIMA CLASE'}
            </div>

            <div className="ta-next-hero-body">
                <div className="ta-next-hero-main">
                    <UrgencyBadge cls={cls} />
                    <div className="ta-next-hero-date">{formatDateLong(cls.date)}</div>
                    <div className="ta-next-hero-time">
                        🕐 {formatHour(cls.hour, cls.time)}
                        <span className="ta-next-hero-tz">CST México</span>
                    </div>
                    {cls.teacher_name && (
                        <div className="ta-next-hero-teacher">👩‍🏫 {cls.teacher_name}</div>
                    )}
                </div>

                {/* De dónde sale la clase: lo primero que hay que saber para
                    entender por qué los botones son otros. */}
                {deEmpresa && (
                    <div className="ta-next-hero-policy" data-prueba="hero-origen-empresa">
                        🏢 {cls.empresa || 'Tu empresa'}
                        {cls.grupo ? ` · ${cls.grupo}` : ''}
                        {cls.personas > 1
                            ? ` · clase de ${cls.personas} personas`
                            : ' · clase de uno a uno'}
                    </div>
                )}

                {/* Si ya avisó, se le dice aquí también: es la tarjeta que mira. */}
                {deEmpresa && cls.aviso && (
                    <div className="ta-next-hero-policy" data-prueba="hero-aviso-hecho">
                        ✅ Avisaste que no podrás ir · {cls.aviso.motivo_texto}
                        {cls.aviso.comprobante === 'pendiente'
                            ? ' · falta tu comprobante médico por WhatsApp' : ''}
                    </div>
                )}

                {/* Por qué no puede cancelar, con palabras. */}
                {deEmpresa && !cls.puede_cancelar && cls.porque_no_cancela && (
                    <div className="ta-next-hero-policy" data-prueba="hero-porque-no-cancela">
                        ℹ️ {cls.porque_no_cancela}
                    </div>
                )}

                {/* Política 24h — preservada de v3, pero SOLO para las clases
                    particulares: una de empresa tiene su propio plazo (las 23:59
                    del día anterior) y su propia razón de ser. */}
                {!isNow && !deEmpresa && (
                    <div className={`ta-next-hero-policy${locked ? ' ta-next-hero-policy--locked' : ''}`}>
                        {locked
                            ? '🚫 Para posponer o cancelar una clase debe hacerse con 24 horas de anticipación. De lo contrario, la opción no está disponible.'
                            : `ℹ️ Puedes modificar hasta 24h antes · ${dh != null && dh > 24 ? `${Math.floor(dh - 24)}h disponibles` : 'cierra pronto'}`
                        }
                    </div>
                )}
            </div>

            <div className="ta-next-hero-actions">
                <button className="ta-btn ta-btn--hero" onClick={() => onEnter(cls)}>
                    🎥 Ver más
                </button>
                {!isNow && deEmpresa && (
                    <div className="ta-next-hero-secondary">
                        <button
                            className="ta-btn ta-btn--ghost ta-btn--sm"
                            data-prueba="hero-no-podre"
                            onClick={() => onAviso(cls, 'aviso')}
                        >
                            🙋 {cls.aviso ? 'Cambiar mi aviso' : 'No podré asistir'}
                        </button>
                        {cls.puede_cancelar && (
                            <button
                                className="ta-btn ta-btn--ghost ta-btn--sm ta-btn--danger-ghost"
                                data-prueba="hero-cancelar-empresa"
                                onClick={() => onAviso(cls, 'cancelar')}
                            >
                                ✕ Cancelar y reagendar
                            </button>
                        )}
                    </div>
                )}

                {!isNow && !deEmpresa && (
                    <div className="ta-next-hero-secondary">
                        <button
                            className="ta-btn ta-btn--ghost ta-btn--sm"
                            onClick={handleModify(onPostpone)}
                            disabled={locked}
                            title={locked ? 'Ventana cerrada' : 'Posponer'}
                        >
                            ⏸ Posponer
                        </button>
                        <button
                            className="ta-btn ta-btn--ghost ta-btn--sm ta-btn--danger-ghost"
                            onClick={handleModify(onCancel)}
                            disabled={locked}
                            title={locked ? 'Ventana cerrada' : 'Cancelar'}
                        >
                            ✕ Cancelar Clase
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  WEEK STRIP — v4: navegación entre semanas + días pasados desbloqueados
// ─────────────────────────────────────────────────────────────────────────────
function WeekStrip({ weekDates, classMap, selectedDay, onSelectDay, todayISO, weekOffset, onWeekChange }) {
    const weekLabel = getWeekLabel(weekOffset, weekDates);

    return (
        <div className="ta-week-strip-wrapper">
            {/* Navegación de semana */}
            <div className="ta-week-nav">
                <button
                    className="ta-week-nav-btn"
                    onClick={() => onWeekChange(weekOffset - 1)}
                    title="Semana anterior"
                    aria-label="Semana anterior"
                >
                    ‹
                </button>
                <div className="ta-week-nav-center">
                    <span className="ta-week-label">{weekLabel}</span>
                    {weekOffset !== 0 && (
                        <button
                            className="ta-week-today-btn"
                            onClick={() => onWeekChange(0)}
                            title="Volver a hoy"
                        >
                            Hoy
                        </button>
                    )}
                </div>
                <button
                    className="ta-week-nav-btn"
                    onClick={() => onWeekChange(weekOffset + 1)}
                    title="Semana siguiente"
                    aria-label="Semana siguiente"
                >
                    ›
                </button>
            </div>

            {/* Días de la semana */}
            <div className="ta-week-strip">
                {weekDates.map((iso, i) => {
                    const [, , d] = iso.split('-').map(Number);
                    const dayCls = classMap[iso] || [];
                    const hasClass = dayCls.length > 0;
                    const isToday = iso === todayISO;
                    const isSelect = iso === selectedDay;
                    const isPast = iso < todayISO;

                    return (
                        <button
                            key={iso}
                            className={[
                                'ta-week-day',
                                isToday ? 'ta-week-day--today' : '',
                                isSelect ? 'ta-week-day--selected' : '',
                                isPast ? 'ta-week-day--past' : '',
                                hasClass ? 'ta-week-day--has-class' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => onSelectDay(isSelect ? null : iso)}
                            title={`${DAYS_FULL[i]}, ${formatDateShort(iso)}${hasClass ? ` · ${dayCls.length} clase${dayCls.length > 1 ? 's' : ''}` : ''}`}
                        >
                            <span className="ta-week-day-name">{DAYS_SHORT[i]}</span>
                            <span className="ta-week-day-num">{d}</span>
                            {hasClass ? (
                                <span className="ta-week-day-dots">
                                    {dayCls.slice(0, 3).map((c, ci) => (
                                        <span
                                            key={ci}
                                            className="ta-week-dot"
                                            style={{ background: STATUS_META[c.status]?.color || '#f5a623' }}
                                        />
                                    ))}
                                </span>
                            ) : (
                                <span className="ta-week-day-empty-dot" />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  AVISO DE UNA CLASE DE EMPRESA
//
//  Rob, 19-09-2026: «⏸ Posponer y ✕ Cancelar Clase son geniales para un alumno
//  que paga sus clases privadas, pero en el caso de las empresas no me sirven.
//  Lo ideal sería que un alumno no pueda asistir por carga laboral,
//  incapacitación, etc. … es válido que un alumno avise y explique el motivo».
//
//  Una clase de empresa no es del alumno: la paga su empresa y, si es grupal, la
//  comparten sus compañeros. Por eso aquí no se pospone ni se cancela a la
//  ligera: se AVISA. Y solo cuando la clase es de uno solo, y antes de las 11:59
//  pm del día anterior, se puede además cancelarla para reorganizarla.
// ─────────────────────────────────────────────────────────────────────────────
const MOTIVOS_EMPRESA = [
    { id: 'medica', icono: '🏥', titulo: 'Incapacidad o tema médico',
      pie: 'Tendrás que enviar tu comprobante por WhatsApp' },
    { id: 'laboral', icono: '💼', titulo: 'Carga de trabajo',
      pie: 'Una junta, un cierre, un imprevisto del trabajo' },
    { id: 'otra', icono: '📌', titulo: 'Un motivo personal',
      pie: 'No hace falta que des detalles si no quieres' },
];

function AvisoEmpresaModal({ cls, modo, onCerrar, onHecho }) {
    const [motivo, setMotivo] = useState('');
    const [nota, setNota] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState(null);
    // Si el navegador bloqueó la pestaña de WhatsApp, el aviso ya está guardado
    // pero a NSL no le ha llegado: se queda aquí el enlace para mandarlo a mano.
    const [pendiente, setPendiente] = useState(null);
    const cancela = modo === 'cancelar';

    const enviar = async () => {
        if (!motivo) return;
        setOcupado(true);
        setError(null);
        // Rob, 19-09-2026: «me tengo que enterar por WhatsApp para tener mejor
        // control de la decisión del alumno y de los empleados». Desde ese día
        // WhatsApp se abre SIEMPRE —cualquier motivo, avisar o cancelar—, no
        // solo con el comprobante médico.
        //
        // La pestaña se abre YA, en el mismo clic, y se le pone el enlace
        // cuando responde el servidor. Abrirla después de esperar la respuesta
        // hace que Safari y los móviles la bloqueen como ventana emergente, y
        // entonces el aviso se guardaba sin que NSL se enterara.
        let pestana = null;
        try { pestana = window.open('', '_blank'); } catch { pestana = null; }
        const cierraPestana = () => { try { pestana?.close(); } catch { /* ya cerrada */ } };
        let data;
        try {
            const idReal = String(cls.id).replace(/^b2b-/, '');
            const res = await fetch(
                `/api/student/clases-empresa/${idReal}/${cancela ? 'cancelar' : 'aviso'}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: JSON.stringify({ motivo, nota: nota.trim() || null }),
                },
            );
            data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // El servidor explica POR QUÉ no se puede, con palabras. Se
                // enseña tal cual en vez de un «error 422» que no ayuda a nadie.
                cierraPestana();
                setError(typeof data.detail === 'string' ? data.detail
                    : 'No se pudo registrar tu aviso. Inténtalo otra vez.');
                setOcupado(false);
                return;
            }
        } catch {
            cierraPestana();
            setError('No se pudo conectar. Revisa tu internet e inténtalo otra vez.');
            setOcupado(false);
            return;
        }
        // Fuera del try a propósito: el aviso YA está guardado, y un fallo de
        // aquí en adelante no puede cerrar la pestaña de WhatsApp ni decirle al
        // alumno «no se pudo conectar». El texto lo escribe el servidor para que
        // diga lo mismo desde cualquier pantalla.
        const enlace = enlaceWhatsApp(data.whatsapp || '');
        let abierta = false;
        if (pestana && !pestana.closed) {
            try { pestana.opener = null; } catch { /* sin acceso */ }
            try { pestana.location.href = enlace; abierta = true; } catch { abierta = false; }
        }
        if (abierta) {
            onHecho(data);
        } else {
            cierraPestana();
            setPendiente({ enlace, data });
            setOcupado(false);
        }
    };

    if (pendiente) {
        return (
            <div className="ta-overlay" role="presentation">
                <div className="ta-modal" role="dialog" aria-modal="true"
                    aria-label="Envía tu aviso por WhatsApp" data-prueba="aviso-empresa">
                    <div className="ta-modal-header">
                        <div>
                            <div className="ta-modal-title">✅ Guardado. Falta un paso</div>
                            <div className="ta-modal-sub">
                                Tu navegador no dejó abrir WhatsApp solo. Pulsa el botón
                                para que NSL reciba tu aviso.
                            </div>
                        </div>
                    </div>
                    <div className="ta-modal-actions">
                        <a className="ta-btn ta-btn--accent" href={pendiente.enlace}
                            target="_blank" rel="noopener noreferrer"
                            data-prueba="aviso-whatsapp-manual"
                            onClick={() => onHecho(pendiente.data)}>
                            Enviar por WhatsApp
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // ── LA MISMA CAJA QUE «POSPONER» Y «CANCELAR CLASE» ─────────────────────
    // Rob, 19-09-2026: «se colocan abajo de todo y parece que el botón no
    // funciona… lo quiero igual que Posponer: un modal que se sobrepone y lo
    // pone enfrente». Tenía razón: este modal usaba `ta-modal-overlay`, una
    // clase que no existe en el CSS, así que no flotaba: se pintaba al final de
    // la página, fuera de la vista. Ahora usa las mismas piezas que los otros
    // (`ta-overlay`, `ta-modal`, `ta-modal-header`, `ta-modal-close`).
    return (
        <div className="ta-overlay" role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget && !ocupado) onCerrar(); }}>
            <div className="ta-modal" role="dialog" aria-modal="true"
                aria-label={cancela ? 'Cancelar la clase' : 'Avisar que no podrás asistir'}
                data-prueba="aviso-empresa">
                <div className="ta-modal-header">
                    <div>
                        <div className="ta-modal-title" style={cancela ? { color: '#ff5e7e' } : undefined}>
                            {cancela ? '✕ Cancelar y reagendar' : '🙋 ¿Por qué no podrás asistir?'}
                        </div>
                        <div className="ta-modal-sub">
                            {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)} CST
                            {cls.grupo ? ` · ${cls.grupo}` : ''}
                        </div>
                    </div>
                    <button className="ta-modal-close" onClick={onCerrar} disabled={ocupado}
                        aria-label="Cerrar">✕</button>
                </div>

                <div className="ta-aviso-motivos">
                    {MOTIVOS_EMPRESA.map((m) => (
                        <button key={m.id} type="button"
                            className={`ta-aviso-motivo${motivo === m.id ? ' ta-aviso-motivo--sel' : ''}`}
                            data-prueba={`motivo-${m.id}`}
                            onClick={() => setMotivo(m.id)}>
                            <span className="ta-aviso-motivo-icono">{m.icono}</span>
                            <span>
                                <strong>{m.titulo}</strong>
                                <span className="ta-aviso-motivo-pie">{m.pie}</span>
                            </span>
                        </button>
                    ))}
                </div>

                <label className="ta-aviso-nota">
                    Cuéntale a tu profesor lo que necesite saber <span>(opcional)</span>
                    <textarea maxLength={300} rows={3} value={nota}
                        data-prueba="aviso-nota"
                        placeholder={cancela
                            ? 'Por ejemplo: ¿qué días te vendrían bien para reponerla?'
                            : 'Por ejemplo: vuelvo el martes y me pongo al día'}
                        onChange={(e) => setNota(e.target.value)} />
                </label>

                {motivo === 'medica' ? (
                    <div className="ta-aviso-nota-medica" data-prueba="aviso-medico">
                        Al confirmar se abre WhatsApp para que envíes tu comprobante
                        médico. Sin él, la falta queda como no justificada.
                    </div>
                ) : motivo ? (
                    <div className="ta-aviso-nota-medica" data-prueba="aviso-wa">
                        Al confirmar se abre WhatsApp con tu aviso ya escrito: envíalo
                        para que NSL {cancela ? 'reorganice la clase contigo' : 'lo sepa'}.
                    </div>
                ) : null}

                {!cancela ? (
                    <div className="ta-aviso-recordatorio">
                        La clase sigue en pie para tus compañeros. Esto es solo para que
                        tu profesor y tu empresa sepan que no estarás.
                    </div>
                ) : null}

                {error ? <div className="ta-aviso-error" data-prueba="aviso-error">{error}</div> : null}

                <div className="ta-modal-actions">
                    <button className="ta-btn ta-btn--ghost" onClick={onCerrar} disabled={ocupado}>
                        Volver
                    </button>
                    <button className={`ta-btn ${cancela ? 'ta-btn--danger' : 'ta-btn--accent'}`}
                        onClick={enviar} disabled={ocupado || !motivo}
                        data-prueba="aviso-confirmar">
                        {ocupado ? 'Un momento…' : (cancela ? 'Cancelar y avisar' : 'Avisar por WhatsApp')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  LOS CAMBIOS QUE HIZO GERENCIA
//
//  Rob, 19-09-2026: «reprogramo y lo realiza genial, pero ¿cómo se entera un
//  alumno corporativo? Si no me ve en WhatsApp, debería indicar el sistema ese
//  cambio». Va ARRIBA de la agenda y no dentro de la lista del día: una clase
//  movida a otra semana, o cancelada, no está en el día que tiene abierto.
//  Se queda hasta que pulsa «Entendido», que es lo que gerencia ve como visto.
// ─────────────────────────────────────────────────────────────────────────────
function CambiosDeGerencia({ cambios, resaltado, onVer, onEntendido }) {
    const refResaltado = useRef(null);
    useEffect(() => {
        if (resaltado && refResaltado.current) {
            refResaltado.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [resaltado, cambios.length]);
    if (!cambios.length) return null;
    return (
        <div className="ta-cambios" data-prueba="cambios-gerencia" role="region"
            aria-label="Cambios en tus clases">
            <div className="ta-cambios-titulo">
                📅 {cambios.length === 1 ? 'Hay un cambio en tus clases' : `Hay ${cambios.length} cambios en tus clases`}
            </div>
            {cambios.map((c) => (
                <div key={c.id} ref={c.id === resaltado ? refResaltado : null}
                    className={`ta-cambio${c.tipo === 'cancelada' ? ' ta-cambio--cancelada' : ''}${c.id === resaltado ? ' ta-cambio--resaltado' : ''}`}
                    data-prueba="cambio" data-cambio={c.id}>
                    <div className="ta-cambio-texto">
                        <strong>{c.titulo}</strong>
                        <span>{c.mensaje}</span>
                    </div>
                    <div className="ta-cambio-acciones">
                        {c.fecha_agenda && (
                            <button className="ta-btn ta-btn--ghost ta-btn--sm" onClick={() => onVer(c)}
                                data-prueba="cambio-ver">
                                Ver el día
                            </button>
                        )}
                        <button className="ta-btn ta-btn--accent ta-btn--sm" onClick={() => onEntendido(c.id)}
                            data-prueba="cambio-entendido">
                            Entendido
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CLASS CARD
// ─────────────────────────────────────────────────────────────────────────────
function ClassCard({ cls, isNext, onEnter, onPostpone, onCancel, onLocked, onAviso }) {
    const effectiveStatus = resolveStatus(cls); // 🔒 Capa 2
    const sm = STATUS_META[effectiveStatus] || STATUS_META.assigned;
    const locked = effectiveStatus === 'assigned' && isWindowClosed(cls);
    const canAct = effectiveStatus === 'assigned';
    // Una clase de empresa no se pospone ni se cancela como una particular: no
    // es del alumno. Sus botones son otros.
    const deEmpresa = cls.origen === 'empresa';

    const handleModify = (fn) => () => {
        if (locked) { onLocked(cls); return; }
        fn(cls);
    };

    return (
        <div className={`ta-class-card${isNext ? ' ta-class-card--next' : ''}`}>
            {isNext && <div className="ta-class-card-next-label">↑ Esta es la próxima</div>}

            <div className="ta-class-card-top">
                <span
                    className="ta-status-pill"
                    style={{ background: sm.pill, color: 'var(--text-primary)', borderColor: `${sm.color}55` }}
                >
                    {sm.icon} {sm.label}
                </span>
                {canAct && <UrgencyBadge cls={cls} />}
            </div>

            <div className="ta-class-card-info">
                <div className="ta-class-card-date">{formatDateLong(cls.date)}</div>
                <div className="ta-class-card-time">🕐 {formatHour(cls.hour, cls.time)} <span className="ta-tz-tag">CST</span></div>
            </div>

            {/* De dónde sale la clase, para que no parezca una particular. */}
            {deEmpresa && (
                <div className="ta-class-card-origen" data-prueba="origen-empresa">
                    🏢 {cls.empresa || 'Tu empresa'}
                    {cls.grupo ? ` · ${cls.grupo}` : ''}
                    {cls.personas > 1 ? ` · ${cls.personas} personas` : ' · clase de uno a uno'}
                </div>
            )}

            {/* Gerencia la cambió y todavía no lo ha visto: se dice EN la
                clase, que es donde mira quien busca su hora. */}
            {deEmpresa && cls.cambio && (
                <div className="ta-cambio-en-clase" data-prueba="cambio-en-clase">
                    🔄 {cls.cambio.titulo}
                    {cls.cambio.tipo === 'reprogramada' && cls.cambio.antes
                        ? ` · antes era el ${formatDateLong(cls.cambio.antes.fecha).toLowerCase()} a las ${formatHour(null, cls.cambio.antes.hora)}`
                        : ''}
                </div>
            )}

            {/* Si ya avisó, se le dice: pedirle dos veces lo mismo es no
                haberle escuchado la primera. */}
            {deEmpresa && cls.aviso && (
                <div className="ta-aviso-hecho" data-prueba="aviso-hecho">
                    ✅ Avisaste que no podrás ir · {cls.aviso.motivo_texto}
                    {cls.aviso.comprobante === 'pendiente'
                        ? ' · falta tu comprobante médico por WhatsApp' : ''}
                    {cls.aviso.a_tiempo ? '' : ' · avisaste fuera del plazo'}
                </div>
            )}

            {canAct && locked && !deEmpresa && (
                <div className="ta-window-warning">
                    🚫 Por política de 24h, ya no es posible posponer o cancelar esta clase.
                </div>
            )}

            {/* Por qué no puede cancelarla, dicho con palabras y no con un botón
                gris sin explicación. */}
            {canAct && deEmpresa && !cls.puede_cancelar && cls.porque_no_cancela && (
                <div className="ta-window-warning" data-prueba="porque-no-cancela">
                    ℹ️ {cls.porque_no_cancela}
                </div>
            )}

            {canAct && deEmpresa && (
                <div className="ta-class-card-actions">
                    <button className="ta-btn ta-btn--accent ta-btn--sm" onClick={() => onEnter(cls)}>
                        🎥 Ver más
                    </button>
                    <button className="ta-btn ta-btn--ghost ta-btn--sm"
                        data-prueba="btn-no-podre"
                        onClick={() => onAviso(cls, 'aviso')}>
                        🙋 {cls.aviso ? 'Cambiar mi aviso' : 'No podré asistir'}
                    </button>
                    {cls.puede_cancelar && (
                        <button className="ta-btn ta-btn--ghost ta-btn--sm ta-btn--danger-ghost"
                            data-prueba="btn-cancelar-empresa"
                            onClick={() => onAviso(cls, 'cancelar')}>
                            ✕ Cancelar y reagendar
                        </button>
                    )}
                </div>
            )}

            {canAct && !deEmpresa && (
                <div className="ta-class-card-actions">
                    <button className="ta-btn ta-btn--accent ta-btn--sm" onClick={() => onEnter(cls)}>
                        🎥 Ver más
                    </button>
                    <button
                        className="ta-btn ta-btn--ghost ta-btn--sm"
                        onClick={handleModify(onPostpone)}
                        disabled={locked}
                    >
                        ⏸ Posponer
                    </button>
                    <button
                        className="ta-btn ta-btn--ghost ta-btn--sm ta-btn--danger-ghost"
                        onClick={handleModify(onCancel)}
                        disabled={locked}
                    >
                        ✕ Cancelar Clase
                    </button>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  HISTORY ITEM — versión minimalista para el historial
// ─────────────────────────────────────────────────────────────────────────────
function HistoryItem({ cls }) {
    const effectiveStatus = resolveStatus(cls); // 🔒 Capa 2
    const sm = STATUS_META[effectiveStatus] || STATUS_META.completed;
    return (
        <div className="ta-history-item">
            <span className="ta-history-dot" style={{ background: sm.color }} title={sm.label} />
            <div className="ta-history-info">
                <span className="ta-history-date">{formatDateLong(cls.date)}</span>
                <span className="ta-history-time">{formatHour(cls.hour, cls.time)}</span>
            </div>
            <span className="ta-history-status" style={{ color: sm.color }}>
                {sm.icon} {sm.label}
            </span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EMPTY STATES
// ─────────────────────────────────────────────────────────────────────────────
function EmptyDayState({ iso, isPast, todayISO }) {
    const isToday = iso === todayISO;
    return (
        <div className="ta-empty-state">
            <div className="ta-empty-icon">{isPast ? '📖' : isToday ? '☀️' : '🗓️'}</div>
            <div className="ta-empty-title">
                {isPast
                    ? 'Sin clases este día'
                    : isToday
                        ? '¡Día libre hoy!'
                        : 'Sin clases programadas'
                }
            </div>
            <div className="ta-empty-sub">
                {isPast
                    ? 'No hubo clases registradas para este día.'
                    : isToday
                        ? 'No tienes clases hoy. Aprovecha para usar Spacy 😎.'
                        : 'Tu coordinador agendará clases para este día próximamente.'
                }
            </div>
        </div>
    );
}

function EmptyAllFuture() {
    return (
        <div className="ta-empty-state">
            <div className="ta-empty-icon">🎯</div>
            <div className="ta-empty-title">Sin clases próximas</div>
            <div className="ta-empty-sub">
                Tu coordinador agendará tus próximas clases pronto. ¡Sigue practicando!
            </div>
        </div>
    );
}

function SelectDayPrompt() {
    return (
        <div className="ta-empty-state ta-empty-state--prompt">
            <div className="ta-empty-icon">👆</div>
            <div className="ta-empty-title">Selecciona un día</div>
            <div className="ta-empty-sub">Toca cualquier día del calendario para ver sus clases.</div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MODAL — Entrar a Clase
// ─────────────────────────────────────────────────────────────────────────────
function EnterClassModal({ cls, onClose }) {
    const dh = diffHours(cls);
    const isNow = dh !== null && dh <= 0;

    return (
        <div className="ta-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="ta-modal">
                <div className="ta-modal-header">
                    <div>
                        <div className="ta-modal-title">🎥 Entrar a tu Clase</div>
                        <div className="ta-modal-sub">{formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)} CST</div>
                    </div>
                    <button className="ta-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="ta-enter-summary">
                    <div>📅 <strong>{formatDateLong(cls.date)}</strong></div>
                    <div>🕐 <strong>{formatHour(cls.hour, cls.time)}</strong> — Zona horaria CST México</div>
                    {cls.teacher_name && <div>👩‍🏫 <strong>{cls.teacher_name}</strong></div>}
                </div>

                <div className={`ta-enter-notice${isNow ? ' ta-enter-notice--now' : ''}`}>
                    {isNow
                        ? '🟢 Tu clase comienza ahora. Tu teacher te envió el link por WhatsApp o correo.'
                        : '⏰ El link de videollamada llega por WhatsApp 15 minutos antes. Revisa también tu correo.'
                    }
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <a
                        href={`https://wa.me/${cls.teacher_phone || '5215610480746'}?text=${encodeURIComponent(`Hola, tengo clase el ${formatDateLong(cls.date)} a las ${formatHour(cls.hour, cls.time)} CST.`)}`}
                        target="_blank" rel="noreferrer"
                        className="ta-btn ta-btn--accent"
                        style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                    >
                        💬 Abrir WhatsApp
                    </a>
                    <button className="ta-btn ta-btn--ghost" onClick={onClose} style={{ flex: 1 }}>Cerrar</button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MINI DATE PICKER — Selector estilizado limitado a un rango
// ─────────────────────────────────────────────────────────────────────────────
const DAYS_HEADER = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MONTHS_MINI = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function MiniDatePicker({ value, onChange, maxDays = 30 }) {
    const today = getCSTNow();
    today.setHours(0, 0, 0, 0);

    // Rango válido: mañana → hoy + maxDays
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 1);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxDays);

    // Generar todas las celdas desde el lunes de la semana de minDate
    // hasta el domingo de la semana de maxDate
    const cells = useMemo(() => {
        const start = new Date(minDate);
        const dow = start.getDay();
        start.setDate(start.getDate() - (dow === 0 ? 6 : dow - 1)); // lunes anterior

        const end = new Date(maxDate);
        const eow = end.getDay();
        if (eow !== 0) end.setDate(end.getDate() + (7 - eow)); // domingo siguiente

        const result = [];
        const cur = new Date(start);
        while (cur <= end) {
            result.push(new Date(cur));
            cur.setDate(cur.getDate() + 1);
        }
        return result;
    }, [maxDays]);

    // Agrupar en semanas de 7
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
    }

    return (
        <div style={{
            background: 'var(--bg-primary, #111)',
            borderRadius: 12,
            border: '1px solid var(--border-color, rgba(128,128,128,0.2))',
            padding: '10px 8px 8px',
            userSelect: 'none',
        }}>
            {/* Leyenda del rango */}
            <div style={{
                textAlign: 'center', fontSize: 10, fontWeight: 700,
                color: 'var(--text-secondary, rgba(255,255,255,.3))', textTransform: 'uppercase',
                letterSpacing: '.08em', marginBottom: 8,
            }}>
                Disponible · próximos {maxDays} días
            </div>

            {/* Cabecera días */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
                {DAYS_HEADER.map((d, i) => (
                    <div key={d} style={{
                        textAlign: 'center', fontSize: 9, fontWeight: 800,
                        color: i >= 5 ? 'var(--ta-weekend, #f5a623)' : 'var(--text-secondary, rgba(255,255,255,.3))',
                        padding: '2px 0',
                    }}>{d}</div>
                ))}
            </div>

            {/* Semanas */}
            {weeks.map((week, wi) => {
                // Mostrar mes cuando cambia
                const firstValid = week.find(d => d >= minDate && d <= maxDate);
                const showMonth = firstValid && (wi === 0 ||
                    firstValid.getMonth() !== weeks[wi - 1].find(d => d >= minDate && d <= maxDate)?.getMonth());

                return (
                    <div key={wi}>
                        {showMonth && (
                            <div style={{
                                fontSize: 9, fontWeight: 700, color: 'var(--ta-step-text, var(--accent, #00ffff))',
                                textTransform: 'uppercase', letterSpacing: '.1em',
                                padding: '4px 2px 2px', opacity: .7,
                            }}>
                                {MONTHS_MINI[firstValid.getMonth()]}
                            </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
                            {week.map((date, di) => {
                                const iso = formatDateISO(date);
                                const isSelected = iso === value;
                                const isValid = date >= minDate && date <= maxDate;
                                const isWeekend = di >= 5;

                                return (
                                    <button
                                        key={iso}
                                        type="button"
                                        onClick={() => isValid && onChange(iso)}
                                        disabled={!isValid}
                                        style={{
                                            padding: '7px 2px',
                                            borderRadius: 8,
                                            border: isSelected
                                                ? '1px solid var(--ta-step-bd, var(--accent, #00ffff))'
                                                : '1px solid transparent',
                                            background: isSelected
                                                ? 'var(--ta-step-bg, rgba(0,255,255,.15))'
                                                : 'transparent',
                                            color: !isValid
                                                ? 'var(--text-disabled, rgba(128,128,128,.4))'
                                                : isSelected
                                                    ? 'var(--ta-step-text, var(--accent, #00ffff))'
                                                    : isWeekend
                                                        ? 'var(--ta-weekend, #f5a623)'
                                                        : 'var(--text-primary, rgba(255,255,255,.75))',
                                            fontSize: 12,
                                            fontWeight: isSelected ? 800 : 500,
                                            cursor: isValid ? 'pointer' : 'default',
                                            textAlign: 'center',
                                            fontFamily: "'JetBrains Mono', monospace",
                                            transition: 'all .12s ease',
                                        }}
                                        onMouseEnter={e => {
                                            if (isValid && !isSelected)
                                                e.currentTarget.style.background = 'var(--hover-bg, rgba(128,128,128,.15))';
                                        }}
                                        onMouseLeave={e => {
                                            if (!isSelected)
                                                e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        {date.getDate()}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Fecha seleccionada */}
            {value && (
                <div style={{
                    marginTop: 8, textAlign: 'center', fontSize: 11,
                    fontWeight: 700, color: 'var(--accent, #00ffff)',
                    borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: 8,
                }}>
                    ✓ {formatDateLong(value)}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MODAL — Posponer
// ─────────────────────────────────────────────────────────────────────────────
// PostponeModal — pide el cambio por WhatsApp. NO mueve la clase: la ajusta gerencia.
function PostponeModal({ cls, teacher, maxDays = 30, onConfirm, onClose, isPending }) {
    const [step, setStep]                   = useState(1);  // 1 = form, 2 = confirmar
    const [reason, setReason]               = useState('');
    const [preferredDate, setPreferredDate] = useState('');
    const [preferredTime, setPreferredTime] = useState('');
    const [done, setDone]                   = useState(false);

    const WA_GERENCIA = '5215610480746';

    function buildWAMessage() {
        // Sin emojis: WhatsApp los pintaba como un rombo con interrogación
        // (Rob, 11-09-2026). Lo que sí llega —negritas y líneas en blanco— es lo
        // que hace legible el mensaje, así que las líneas opcionales se quitan
        // con `null` y NO con `filter(Boolean)`, que se llevaba también las
        // líneas en blanco y dejaba el mensaje apelmazado.
        const lineas = [
            '*Solicitud de cambio de clase*',
            '',
            `*Clase:* ${formatDateLong(cls.date)} · ${formatHour(cls.hour, cls.time)} CST`,
            cls.teacher_name ? `*Teacher:* ${cls.teacher_name}` : null,
        ];
        const propuesta = [
            reason        ? `*Motivo:* ${reason}` : null,
            preferredDate ? `*Fecha preferida:* ${preferredDate}` : null,
            preferredTime ? `*Horario preferido:* ${preferredTime}` : null,
        ].filter((l) => l !== null);
        if (propuesta.length) lineas.push('', ...propuesta);
        lineas.push('', '_Por favor confirmen disponibilidad con el teacher._', '_Non Stop Learning_');
        return lineas.filter((l) => l !== null).join('\n');
    }

    async function handleConfirmAndNotify() {
        // 1. Abrir WhatsApp primero
        window.open(
            enlaceWhatsApp(buildWAMessage()),
            '_blank'
        );
        // 2. NADA MAS. La clase no se toca: Rob quiere que todo cambio pase
        //    por WhatsApp a gerencia, que habla con el profesor y lo ajusta en
        //    el CRM. La ruta que la movia directo esta cerrada en el servidor.
        setDone(true);
    }

    return (
        <div className="ta-overlay"
            onClick={e => {
                if (e.target !== e.currentTarget) return; // 🔒 EVITAR CIERRE AL CLICKEAR DENTRO
                // Bloquear cierre con clic fuera si están en paso 2 sin completar
                if (step === 2 && !done) return;
                if (!isPending) onClose();
            }}>
            <div className="ta-modal">

                {/* ── Indicador de pasos ───────────────────────────── */}
                {!done && (
                    <div style={{
                        display: 'flex', gap: 8, alignItems: 'center',
                        marginBottom: 18,
                    }}>
                        {[1, 2].map(n => (
                            <React.Fragment key={n}>
                                <div style={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12, fontWeight: 800,
                                    background: step >= n
                                        ? 'var(--ta-step-bg, rgba(0,255,255,.2))'
                                        : 'rgba(255,255,255,.06)',
                                    color: step >= n ? 'var(--ta-step-text, var(--accent, #00ffff))' : 'rgba(255,255,255,.3)',
                                    boxShadow: step >= n
                                        ? '0 0 0 1px var(--ta-step-bd, rgba(0,255,255,.4))'
                                        : '0 0 0 1px rgba(255,255,255,.08)',
                                    transition: 'all .2s',
                                }}>
                                    {n}
                                </div>
                                {n < 2 && (
                                    <div style={{
                                        flex: 1, height: 2, borderRadius: 2,
                                        background: step > n
                                            ? 'var(--ta-step-bd, rgba(0,255,255,.4))'
                                            : 'rgba(255,255,255,.07)',
                                        transition: 'background .3s',
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                        <span style={{
                            marginLeft: 8, fontSize: 11,
                            color: 'rgba(255,255,255,.35)', fontWeight: 600,
                        }}>
                            Paso {step} de 2
                        </span>
                    </div>
                )}

                {/* PASO 1 — Datos */}
                {step === 1 && (
                    <>
                        <div className="ta-modal-header">
                            <div>
                                <div className="ta-modal-title">
                                    {E.pause} Cambiar Clase
                                </div>
                                <div className="ta-modal-sub">
                                    {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)}
                                </div>
                            </div>
                            <button className="ta-modal-close" onClick={onClose}>✕</button>
                        </div>

                        <div className="ta-modal-notice" style={{ marginBottom: 16 }}>
                            {E.info} Indica el motivo y la fecha/horario que te
                            funciona. NSL coordinará con tu teacher.
                        </div>

                        <div className="ta-field">
                            <label className="ta-field-label">¿Por qué necesitas cambiarla? *</label>
                            <textarea
                                className="ta-field-input ta-field-input--ta"
                                rows={2}
                                placeholder="Ej: Reunión de trabajo, viaje, emergencia..."
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="ta-field">
                            <label className="ta-field-label">
                                ¿Qué fecha te quedaría bien? (sugerencia)
                            </label>
                            <MiniDatePicker
                                value={preferredDate}
                                onChange={setPreferredDate}
                                maxDays={maxDays}
                            />
                        </div>

                        <div className="ta-field">
                            <label className="ta-field-label">
                                ¿Qué horario prefieres? (sugerencia)
                            </label>
                            <select
                                className="ta-field-input"
                                value={preferredTime}
                                onChange={e => setPreferredTime(e.target.value)}
                            >
                                <option value="">— Sin preferencia —</option>
                                {['7:00 am','8:00 am','9:00 am','10:00 am',
                                  '11:00 am','12:00 pm','1:00 pm','2:00 pm',
                                  '3:00 pm','4:00 pm','5:00 pm','6:00 pm',
                                  '7:00 pm','8:00 pm','9:00 pm',
                                ].map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className="ta-modal-actions">
                            <button className="ta-btn ta-btn--ghost" onClick={onClose}>
                                Cancelar
                            </button>
                            <button
                                className="ta-btn ta-btn--accent"
                                onClick={() => setStep(2)}
                                disabled={!reason.trim()}
                            >
                                Siguiente →
                            </button>
                        </div>
                    </>
                )}

                {/* PASO 2 — Confirmar + WhatsApp (obligatorio) */}
                {step === 2 && !done && (
                    <>
                        <div className="ta-modal-header">
                            <div>
                                <div className="ta-modal-title">
                                    Confirmar solicitud
                                </div>
                                <div className="ta-modal-sub">
                                    {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)}
                                </div>
                            </div>
                        </div>

                        <div style={{
                            padding: '12px 14px', borderRadius: 12,
                            background: 'rgba(0,255,255,.07)',
                            border: '1px solid rgba(0,255,255,.2)',
                            marginBottom: 16, fontSize: 12,
                            color: 'rgba(255,255,255,.7)', lineHeight: 1.7,
                        }}>
                            <div style={{
                                fontWeight: 800, color: 'var(--accent, #00ffff)',
                                marginBottom: 6, fontSize: 13,
                            }}>
                                Resumen del cambio:
                            </div>
                            <div>{E.calendar} {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)}</div>
                            {preferredDate && <div>{E.calendar} Sugerido: {preferredDate} {preferredTime}</div>}
                            <div>{E.info} {reason}</div>
                        </div>

                        <div style={{
                            padding: '10px 14px', borderRadius: 10,
                            background: 'rgba(37,211,102,.06)',
                            border: '1px solid rgba(37,211,102,.2)',
                            fontSize: 12, color: 'rgba(255,255,255,.6)',
                            lineHeight: 1.6, marginBottom: 20,
                        }}>
                            {E.phone} Se enviará tu solicitud a NSL por WhatsApp. <strong style={{ color: '#fff' }}>Tu clase no cambia</strong> hasta que gerencia te confirme.
                        </div>

                        <button
                            onClick={handleConfirmAndNotify}
                            disabled={isPending}
                            style={{
                                width: '100%', padding: '14px',
                                borderRadius: 14, border: 'none',
                                cursor: isPending ? 'not-allowed' : 'pointer',
                                background: 'linear-gradient(135deg, rgba(0,255,255,.2), rgba(37,211,102,.15))',
                                boxShadow: '0 0 0 1px rgba(37,211,102,.35), 0 4px 20px rgba(37,211,102,.1)',
                                fontFamily: 'inherit', fontWeight: 800,
                                fontSize: 14, color: '#fff',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 10,
                                transition: 'all .2s',
                                marginBottom: 10,
                            }}
                        >
                            {isPending
                                ? '⏳ Procesando...'
                                : `${E.phone} Confirmar y notificar a NSL`
                            }
                        </button>

                        <button
                            className="ta-btn ta-btn--ghost"
                            onClick={() => setStep(1)}
                            disabled={isPending}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            ← Regresar y editar
                        </button>
                    </>
                )}

                {/* COMPLETADO */}
                {done && (
                    <>
                        <div style={{ textAlign: 'center', padding: '24px 0 20px' }}>
                            <div style={{ fontSize: 52, marginBottom: 12 }}>{E.check}</div>
                            <div style={{
                                fontSize: 16, fontWeight: 800,
                                color: 'var(--text-primary)', marginBottom: 8,
                            }}>
                                Solicitud enviada
                            </div>
                            <div style={{
                                fontSize: 12, color: 'rgba(255,255,255,.4)',
                                lineHeight: 1.6,
                            }}>
                                Tu solicitud le llegó a NSL por WhatsApp.<br />
                                Tu clase sigue igual hasta que te confirmemos.
                            </div>
                        </div>
                        <button
                            className="ta-btn ta-btn--ghost"
                            onClick={onClose}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            Cerrar
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MODAL — Cancelar
// ─────────────────────────────────────────────────────────────────────────────
function CancelModal({ cls, onConfirm, onClose, isPending }) {
    const [step, setStep]       = useState(1);  // 1 = form, 2 = confirmar
    const [reason, setReason]   = useState('');
    const [done, setDone]       = useState(false);

    const WA_GERENCIA = '5215610480746';

    function buildWAMessage() {
        // Sin emojis: WhatsApp los pintaba como un rombo con interrogación
        // (Rob, 11-09-2026). Las negritas y las líneas en blanco sí llegan.
        const lineas = [
            '*Solicitud de cancelación de clase*',
            '',
            `*Fecha:* ${formatDateLong(cls.date)} · ${formatHour(cls.hour, cls.time)} CST`,
            cls.teacher_name ? `*Teacher:* ${cls.teacher_name}` : null,
        ];
        if (reason) lineas.push('', `*Motivo:* ${reason}`);
        lineas.push('', '_Non Stop Learning_');
        return lineas.filter((l) => l !== null).join('\n');
    }

    // Este botón SOLO abre WhatsApp: la cancelación la decide gerencia.
    async function handleConfirmAndNotify() {
        // 1. Abrir WhatsApp primero (el usuario ve que el mensaje se envía)
        window.open(
            enlaceWhatsApp(buildWAMessage()),
            '_blank'
        );
        // 2. NADA MAS. La clase no se cancela aqui: gerencia lo habla con el
        //    profesor por WhatsApp y lo ajusta en el CRM.
        setDone(true);
    }

    return (
        <div className="ta-overlay"
            onClick={e => {
                if (e.target !== e.currentTarget) return; // 🔒 EVITAR CIERRE AL CLICKEAR DENTRO
                // Bloquear cierre con clic fuera si están en paso 2 sin completar
                if (step === 2 && !done) return;
                if (!isPending) onClose();
            }}>
            <div className="ta-modal">

                {/* ── Indicador de pasos ───────────────────────────── */}
                {!done && (
                    <div style={{
                        display: 'flex', gap: 8, alignItems: 'center',
                        marginBottom: 18,
                    }}>
                        {[1, 2].map(n => (
                            <React.Fragment key={n}>
                                <div style={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12, fontWeight: 800,
                                    background: step >= n
                                        ? 'rgba(255,94,126,.2)'
                                        : 'rgba(255,255,255,.06)',
                                    color: step >= n ? '#ff5e7e' : 'rgba(255,255,255,.3)',
                                    boxShadow: step >= n
                                        ? '0 0 0 1px rgba(255,94,126,.4)'
                                        : '0 0 0 1px rgba(255,255,255,.08)',
                                    transition: 'all .2s',
                                }}>
                                    {n}
                                </div>
                                {n < 2 && (
                                    <div style={{
                                        flex: 1, height: 2, borderRadius: 2,
                                        background: step > n
                                            ? 'rgba(255,94,126,.4)'
                                            : 'rgba(255,255,255,.07)',
                                        transition: 'background .3s',
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                        <span style={{
                            marginLeft: 8, fontSize: 11,
                            color: 'rgba(255,255,255,.35)', fontWeight: 600,
                        }}>
                            Paso {step} de 2
                        </span>
                    </div>
                )}

                {/* PASO 1 — Motivo */}
                {step === 1 && (
                    <>
                        <div className="ta-modal-header">
                            <div>
                                <div className="ta-modal-title" style={{ color: '#ff5e7e' }}>
                                    {E.cross} Cancelar Clase
                                </div>
                                <div className="ta-modal-sub">
                                    {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)}
                                </div>
                            </div>
                            <button className="ta-modal-close" onClick={onClose}>✕</button>
                        </div>

                        <div className="ta-modal-notice ta-modal-notice--danger"
                            style={{ marginBottom: 16 }}>
                            {E.warn} Las cancelaciones con menos de 24h pueden
                            contar como clase consumida según tu plan.
                        </div>

                        <div className="ta-field">
                            <label className="ta-field-label">¿Por qué cancelas? *</label>
                            <textarea
                                className="ta-field-input ta-field-input--ta"
                                rows={3}
                                placeholder="Ej: Emergencia, enfermedad, compromiso de trabajo..."
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="ta-modal-actions">
                            <button className="ta-btn ta-btn--ghost" onClick={onClose}>
                                Volver
                            </button>
                            <button
                                className="ta-btn ta-btn--danger"
                                onClick={() => setStep(2)}
                                disabled={!reason.trim()}
                            >
                                Siguiente →
                            </button>
                        </div>
                    </>
                )}

                {/* PASO 2 — Confirmar + WhatsApp (obligatorio) */}
                {step === 2 && !done && (
                    <>
                        <div className="ta-modal-header">
                            <div>
                                <div className="ta-modal-title" style={{ color: '#ff5e7e' }}>
                                    {E.cross} Confirmar cancelación
                                </div>
                                <div className="ta-modal-sub">
                                    {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)}
                                </div>
                            </div>
                        </div>

                        {/* Resumen de lo que se va a hacer */}
                        <div style={{
                            padding: '12px 14px', borderRadius: 12,
                            background: 'rgba(255,94,126,.07)',
                            border: '1px solid rgba(255,94,126,.2)',
                            marginBottom: 16, fontSize: 12,
                            color: 'rgba(255,255,255,.7)', lineHeight: 1.7,
                        }}>
                            <div style={{
                                fontWeight: 800, color: '#ff5e7e',
                                marginBottom: 6, fontSize: 13,
                            }}>
                                Resumen de cancelación:
                            </div>
                            <div>{E.calendar} {formatDateLong(cls.date)} · {formatHour(cls.hour, cls.time)}</div>
                            {cls.teacher_name && <div>{E.teacher} {cls.teacher_name}</div>}
                            <div>{E.info} {reason}</div>
                        </div>

                        {/* Instrucción clara */}
                        <div style={{
                            padding: '10px 14px', borderRadius: 10,
                            background: 'rgba(37,211,102,.06)',
                            border: '1px solid rgba(37,211,102,.2)',
                            fontSize: 12, color: 'rgba(255,255,255,.6)',
                            lineHeight: 1.6, marginBottom: 20,
                        }}>
                            {E.phone} Al presionar el botón se enviará tu solicitud
                            a NSL por WhatsApp. <strong style={{ color: '#fff' }}>Tu clase no
                            se cancela</strong> hasta que gerencia te confirme.
                            <div style={{
                                marginTop: 6, fontSize: 11,
                                color: 'rgba(255,255,255,.3)',
                            }}>
                                Ambos pasos son necesarios para completar el proceso.
                            </div>
                        </div>

                        {/* Botón principal — hace TODO */}
                        <button
                            onClick={handleConfirmAndNotify}
                            disabled={isPending}
                            style={{
                                width: '100%', padding: '14px',
                                borderRadius: 14, border: 'none',
                                cursor: isPending ? 'not-allowed' : 'pointer',
                                background: 'linear-gradient(135deg, rgba(255,94,126,.2), rgba(37,211,102,.15))',
                                boxShadow: '0 0 0 1px rgba(37,211,102,.35), 0 4px 20px rgba(37,211,102,.1)',
                                fontFamily: 'inherit', fontWeight: 800,
                                fontSize: 14, color: '#fff',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 10,
                                transition: 'all .2s',
                                marginBottom: 10,
                            }}
                        >
                            {isPending
                                ? '⏳ Procesando...'
                                : `${E.phone} Confirmar cancelación y notificar a NSL`
                            }
                        </button>

                        {/* Volver — sin cancelar */}
                        <button
                            className="ta-btn ta-btn--ghost"
                            onClick={() => setStep(1)}
                            disabled={isPending}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            ← Regresar y editar
                        </button>
                    </>
                )}

                {/* COMPLETADO */}
                {done && (
                    <>
                        <div style={{ textAlign: 'center', padding: '24px 0 20px' }}>
                            <div style={{ fontSize: 52, marginBottom: 12 }}>{E.check}</div>
                            <div style={{
                                fontSize: 16, fontWeight: 800,
                                color: 'var(--text-primary)', marginBottom: 8,
                            }}>
                                ¡Todo listo!
                            </div>
                            <div style={{
                                fontSize: 12, color: 'rgba(255,255,255,.4)',
                                lineHeight: 1.6,
                            }}>
                                Tu solicitud de cancelación le llegó a NSL por WhatsApp.<br />
                                Tu clase sigue igual hasta que te confirmemos.
                            </div>
                        </div>
                        <button
                            className="ta-btn ta-btn--ghost"
                            onClick={onClose}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            Cerrar
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MODAL — Ventana 24h cerrada (política preservada de v3)
// ─────────────────────────────────────────────────────────────────────────────
function LockedModal({ cls, onClose }) {
    const msg = encodeURIComponent(
        `Hola, necesito ayuda urgente con mi clase del ${formatDateLong(cls.date)} a las ${formatHour(cls.hour, cls.time)} CST.`
    );
    return (
        <div className="ta-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="ta-modal">
                <div className="ta-modal-header">
                    <div>
                        <div className="ta-modal-title" style={{ color: '#f5a623' }}>⏰ Cambio no disponible</div>
                        <div className="ta-modal-sub">{formatDateLong(cls.date)}</div>
                    </div>
                    <button className="ta-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="ta-modal-notice ta-modal-notice--warning">
                    <strong>El periodo de cambios ya cerró.</strong> Las modificaciones deben realizarse con
                    al menos <strong>24 horas de anticipación</strong> para que tu teacher pueda reorganizar
                    su agenda. ¿Tienes una emergencia? Contáctanos directamente.
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <a
                        href={`https://wa.me/5215610480746?text=${msg}`}
                        target="_blank" rel="noreferrer"
                        className="ta-btn ta-btn--accent"
                        style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                    >
                        💬 Contactar Gerencia
                    </a>
                    <button className="ta-btn ta-btn--ghost" onClick={onClose} style={{ flex: 1 }}>Entendido</button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────
export default function TeacherAgendaTab() {
    const {
        teacher, classes, stats, cambios, marcaVisto,
        isLoading, error,
        refetch, postponeClass, cancelClass,
    } = useTeacherAgenda();

    // Si llega desde la tarjeta del chat (`?cambio=ID`), ese aviso se resalta y
    // la agenda salta a su día: «Ver en mi agenda» tiene que llevar a la clase,
    // no a la portada. Se lee de la ruta y no solo al montarse: con la agenda
    // ya abierta, pulsar la tarjeta del chat cambia la ruta sin remontarla.
    const location = useLocation();
    const resaltado = useMemo(() => {
        const v = Number(new URLSearchParams(location.search).get('cambio'));
        return Number.isFinite(v) && v > 0 ? v : null;
    }, [location.search]);

    // `?dia=2026-09-22`: el día EXACTO del que se está hablando. Rob,
    // 19-09-2026: «el botón funciona muy bien en ir a la sección, solo quiero
    // ver si también puede ir al día exacto del calendario». Lo usa el botón de
    // Spacy y el de un cambio de clase; si el día no es de esta semana, la tira
    // de la semana salta también.
    const diaPedido = useMemo(() => {
        const v = new URLSearchParams(location.search).get('dia');
        return /^\d{4}-\d{2}-\d{2}$/.test(v || '') ? v : null;
    }, [location.search]);

    // ── Anclas de fecha ──────────────────────────────────────────────────────
    const todayISO = useMemo(() => formatDateISO(getCSTNow()), []);

    // ── UI state ─────────────────────────────────────────────────────────────
    // v4: por defecto mostramos HOY (no null)
    const [selectedWeekDay, setSelectedWeekDay] = useState(todayISO);
    const [weekOffset, setWeekOffset] = useState(0);
    const [showAllFuture, setShowAllFuture] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [enterTarget, setEnterTarget] = useState(null);
    const [postponeTarget, setPostponeTarget] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [lockedTarget, setLockedTarget] = useState(null);
    const [avisoTarget, setAvisoTarget] = useState(null);
    const [actionPending, setActionPending] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((msg, type = 'ok') => setToast({ msg, type }), []);

    // ── Semana calculada por offset ──────────────────────────────────────────
    const weekDates = useMemo(() => {
        const mon = getMondayForOffset(weekOffset);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(mon);
            d.setDate(d.getDate() + i);
            return formatDateISO(d);
        });
    }, [weekOffset]);

    // ── Navegación de semana ─────────────────────────────────────────────────
    const handleWeekChange = useCallback((newOffset) => {
        setWeekOffset(newOffset);
        setShowAllFuture(false);
        // Si volvemos a esta semana → reselectamos hoy
        // Si vamos a otra semana → limpiamos selección
        if (newOffset === 0) {
            setSelectedWeekDay(todayISO);
        } else {
            setSelectedWeekDay(null);
        }
    }, [todayISO]);

    // ── Partición de clases ──────────────────────────────────────────────────
    const upcoming = useMemo(() =>
        classes
            .filter(c => {
                if (c.status !== 'assigned' || !c.date) return false;
                if (c.date > todayISO) return true;   // fecha futura → siempre incluir
                if (c.date < todayISO) return false;  // fecha pasada → excluir
                // Mismo día: solo incluir si no terminó hace más de 1h
                const h = diffHours(c);
                return h !== null && h > -1;
            })
            .sort((a, b) => a.date !== b.date
                ? a.date.localeCompare(b.date)
                : (a.hour || 0) - (b.hour || 0)
            ),
        [classes, todayISO]
    );

    const nextClass = upcoming[0] || null;

    // classMap con TODAS las clases (pasadas + futuras) para el strip
    const classMapAll = useMemo(() => {
        const map = {};
        classes.forEach(c => {
            if (!c.date) return;
            if (!map[c.date]) map[c.date] = [];
            map[c.date].push(c);
        });
        return map;
    }, [classes]);

    const thisWeekCount = useMemo(
        () => weekDates.filter(iso => (classMapAll[iso] || []).length > 0).length,
        [weekDates, classMapAll]
    );

    // Clases a mostrar en la sección principal
    const displayClasses = useMemo(() => {
        if (showAllFuture) return upcoming;
        if (!selectedWeekDay) return [];
        // Día seleccionado: muestra TODAS las clases de ese día (past o future)
        return (classMapAll[selectedWeekDay] || [])
            .slice()
            .sort((a, b) => (a.hour || 0) - (b.hour || 0));
    }, [upcoming, classMapAll, selectedWeekDay, showAllFuture]);

    // Historial colapsable (clases no-assigned o anteriores a hoy)
    const history = useMemo(() =>
        classes
            .filter(c => {
                if (c.status !== 'assigned') return true;    // completed/cancelled → siempre historial
                if (!c.date) return false;
                if (c.date < todayISO) return true;          // días anteriores → historial
                if (c.date > todayISO) return false;         // días futuros → jamás historial
                // Mismo día: solo si ya terminó (más de 1h desde que inició)
                const h = diffHours(c);
                return h !== null && h < -1;
            })
            .sort((a, b) => {
                if (!a.date && !b.date) return 0;
                if (!a.date) return -1;
                if (!b.date) return 1;
                return b.date.localeCompare(a.date);  // más reciente primero
            }),
        [classes, todayISO]
    );

    const isPastDay = selectedWeekDay && selectedWeekDay < todayISO;

    const irAlDia = useCallback((iso) => {
        if (!iso) return;
        setWeekOffset(offsetDeSemana(iso));
        setShowAllFuture(false);
        setSelectedWeekDay(iso);
    }, []);

    // El día pedido en la ruta manda: se abre en cuanto llega la agenda.
    const diaSaltado = useRef(null);
    useEffect(() => {
        if (!diaPedido || isLoading || diaSaltado.current === diaPedido) return;
        diaSaltado.current = diaPedido;
        irAlDia(diaPedido);
    }, [diaPedido, isLoading, irAlDia]);

    // Una vez por aviso pedido, cuando ya llegó la agenda: saltar a su día y
    // contarlo como visto. Llegó pulsando «Ver en mi agenda» y lo tiene delante,
    // resaltado: ya se enteró.
    //
    // Se RETIENE en pantalla hasta que pulse «Entendido»: en cuanto el servidor
    // lo apunta como visto, la siguiente lectura de la agenda ya no lo trae, y
    // el aviso desaparecía mientras el alumno lo estaba leyendo.
    const [retenido, setRetenido] = useState(null);
    const saltado = useRef(null);
    useEffect(() => {
        if (!resaltado || isLoading || saltado.current === resaltado) return;
        const c = cambios.find((x) => x.id === resaltado);
        saltado.current = resaltado;
        if (!c) return;
        setRetenido(c);
        if (c.fecha_agenda) irAlDia(c.fecha_agenda);
        fetch(`/api/student/cambios-clase/${c.id}/visto`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }).catch(() => { /* sin red: queda el «Entendido» */ });
    }, [resaltado, isLoading, cambios, irAlDia]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    async function handlePostpone(newDate, reason) {
        setActionPending(true);
        try {
            await postponeClass(postponeTarget.id, newDate, reason);
            showToast('Clase pospuesta correctamente 📅');
            // setPostponeTarget(null); // ← NO CERRAR: el modal tiene su propio flujo de éxito
        } catch (err) {
            showToast(err.message, 'err');
        } finally {
            setActionPending(false);
        }
    }

    async function handleCancel(reason) {
        setActionPending(true);
        try {
            await cancelClass(cancelTarget.id, reason);
            showToast('Clase cancelada');
            // setCancelTarget(null); // ← NO CERRAR: el modal tiene su propio flujo de éxito
        } catch (err) {
            showToast(err.message, 'err');
        } finally {
            setActionPending(false);
        }
    }

    const cambiosVisibles = useMemo(() => (
        retenido && !cambios.some((x) => x.id === retenido.id)
            ? [retenido, ...cambios] : cambios
    ), [retenido, cambios]);

    // La marca de la tarjeta también se queda mientras el aviso siga delante.
    const conRetenido = useCallback((cls) => (
        retenido && !cls.cambio && cls.id === `b2b-${retenido.clase_id}`
            ? { ...cls, cambio: retenido } : cls
    ), [retenido]);

    // Título de la sección de clases
    const sectionTitle = showAllFuture
        ? 'Todas las clases futuras'
        : selectedWeekDay
            ? `${isPastDay ? 'Historial' : 'Clases'} · ${formatDateShort(selectedWeekDay)}`
            : 'Clases';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="ta-root">

            {/* ── TOP BAR ─────────────────────────────────────────────────── */}
            <div className="ta-topbar">
                <TeacherChip teacher={teacher} isLoading={isLoading} />
                <button
                    className="ta-btn ta-btn--ghost ta-btn--sm"
                    onClick={refetch}
                    disabled={isLoading}
                    title="Actualizar"
                >
                    {isLoading ? '⏳' : '🔄'}
                </button>
            </div>

            {/* Error banner */}
            {error && !isLoading && (
                <div className="ta-error-banner">
                    ⚠️ Problema de conexión — datos pueden estar desactualizados.
                </div>
            )}

            <CambiosDeGerencia
                cambios={cambiosVisibles}
                resaltado={resaltado}
                onVer={(c) => irAlDia(c.fecha_agenda)}
                onEntendido={(id) => {
                    if (retenido && retenido.id === id) setRetenido(null);
                    marcaVisto(id);
                }}
            />

            {/* ── STATS ROW ───────────────────────────────────────────────── */}
            <StatsRow stats={stats} isLoading={isLoading} weekCount={thisWeekCount} />

            {/* ── LAYOUT PRINCIPAL: Hero (izq) + Calendario/Lista (der) ─── */}
            <div className="ta-main-grid">

                {/* Columna izquierda: Next class hero */}
                <div className="ta-left-col">
                    {!isLoading ? (
                        <NextClassHero
                            cls={nextClass}
                            onEnter={setEnterTarget}
                            onPostpone={setPostponeTarget}
                            onCancel={setCancelTarget}
                            onLocked={setLockedTarget}
                            onAviso={(c, modo) => setAvisoTarget({ cls: c, modo })}
                        />
                    ) : (
                        <div className="ta-next-hero-skeleton">
                            <Skel w="30%" h={11} br={4} style={{ marginBottom: 10 }} />
                            <Skel w="55%" h={28} br={8} style={{ marginBottom: 8 }} />
                            <Skel w="40%" h={16} br={4} style={{ marginBottom: 6 }} />
                            <Skel w="35%" h={14} br={4} style={{ marginBottom: 20 }} />
                            <Skel w="100%" h={44} br={12} />
                        </div>
                    )}
                </div>

                {/* Columna derecha: Semana + Lista de clases */}
                <div className="ta-right-col">

                    {/* Week strip con navegación */}
                    <WeekStrip
                        weekDates={weekDates}
                        classMap={classMapAll}
                        selectedDay={selectedWeekDay}
                        onSelectDay={(iso) => {
                            setSelectedWeekDay(iso);
                            setShowAllFuture(false);
                        }}
                        todayISO={todayISO}
                        weekOffset={weekOffset}
                        onWeekChange={handleWeekChange}
                    />

                    {/* Lista de clases del día seleccionado */}
                    <div className="ta-section">
                        <div className="ta-section-header">
                            <h3 className="ta-section-title">
                                {sectionTitle}
                                {!isLoading && displayClasses.length > 0 && (
                                    <span className="ta-section-count">{displayClasses.length}</span>
                                )}
                            </h3>

                            {/* Botón "Ver todas futuras" / "← Vista semanal" */}
                            {!isLoading && (
                                showAllFuture ? (
                                    <button
                                        className="ta-btn ta-btn--ghost ta-btn--sm"
                                        onClick={() => {
                                            setShowAllFuture(false);
                                            setSelectedWeekDay(todayISO);
                                            setWeekOffset(0);
                                        }}
                                    >
                                        ← Vista semanal
                                    </button>
                                ) : (
                                    <button
                                        className="ta-btn ta-btn--ghost ta-btn--sm"
                                        onClick={() => {
                                            setShowAllFuture(true);
                                            setSelectedWeekDay(null);
                                        }}
                                        title={`Ver las ${upcoming.length} clases futuras`}
                                    >
                                        Ver todas →
                                    </button>
                                )
                            )}
                        </div>

                        <div className="ta-class-list">
                            {isLoading ? (
                                [0, 1].map(i => (
                                    <div key={i} className="ta-class-card-skel">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                            <Skel w={90} h={22} br={50} />
                                            <Skel w={60} h={18} br={50} />
                                        </div>
                                        <Skel w="70%" h={18} br={4} style={{ marginBottom: 6 }} />
                                        <Skel w="40%" h={14} br={4} />
                                    </div>
                                ))
                            ) : showAllFuture && displayClasses.length === 0 ? (
                                <EmptyAllFuture />
                            ) : !showAllFuture && !selectedWeekDay ? (
                                <SelectDayPrompt />
                            ) : displayClasses.length === 0 ? (
                                <EmptyDayState
                                    iso={selectedWeekDay}
                                    isPast={isPastDay}
                                    todayISO={todayISO}
                                />
                            ) : (
                                displayClasses.map(conRetenido).map((cls, idx) => (
                                    <ClassCard
                                        key={cls.id || `${cls.date}-${cls.hour}`}
                                        cls={cls}
                                        isNext={!showAllFuture && idx === 0 && cls.status === 'assigned' && cls.date >= todayISO}
                                        onEnter={setEnterTarget}
                                        onPostpone={setPostponeTarget}
                                        onCancel={setCancelTarget}
                                        onLocked={setLockedTarget}
                                        onAviso={(c, modo) => setAvisoTarget({ cls: c, modo })}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── HISTORIAL (colapsable) ───────────────────────────────────── */}
            {!isLoading && (
                <div className="ta-history-section">
                    <button
                        className="ta-history-toggle"
                        onClick={() => setHistoryOpen(v => !v)}
                        aria-expanded={historyOpen}
                    >
                        <span className="ta-history-toggle-icon">{historyOpen ? '▲' : '▼'}</span>
                        <span>{historyOpen ? 'Ocultar historial' : 'Historial de clases'}</span>
                        {history.length > 0 && (
                            <span className="ta-history-count">{history.length}</span>
                        )}
                    </button>

                    {historyOpen && (
                        <div className="ta-history-list">
                            {history.length === 0 ? (
                                <div className="ta-history-empty">Aún no hay clases en el historial.</div>
                            ) : (
                                history.map(cls => (
                                    <HistoryItem
                                        key={cls.id || `${cls.date}-${cls.hour}`}
                                        cls={cls}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── MODALES ─────────────────────────────────────────────────── */}
            {enterTarget && <EnterClassModal cls={enterTarget} onClose={() => setEnterTarget(null)} />}
            {postponeTarget && <PostponeModal cls={postponeTarget} teacher={teacher} onConfirm={handlePostpone} onClose={() => setPostponeTarget(null)} isPending={actionPending} />}
            {cancelTarget && <CancelModal cls={cancelTarget} onConfirm={handleCancel} onClose={() => setCancelTarget(null)} isPending={actionPending} />}
            {lockedTarget && <LockedModal cls={lockedTarget} onClose={() => setLockedTarget(null)} />}
            {avisoTarget && (
                <AvisoEmpresaModal
                    cls={avisoTarget.cls}
                    modo={avisoTarget.modo}
                    onCerrar={() => setAvisoTarget(null)}
                    onHecho={(data) => {
                        setAvisoTarget(null);
                        setToast({ msg: data?.mensaje || 'Aviso registrado', type: 'ok' });
                        // Se vuelve a pedir la agenda: la tarjeta tiene que
                        // reflejar el aviso, no fiarse de lo que cree la pantalla.
                        // (Llamaba a `fetchAgenda`, que aquí no existe: el error
                        // se tragaba en silencio y la tarjeta no se refrescaba.)
                        refetch();
                    }}
                />
            )}

            {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
        </div>
    );
}